import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface Props {
  open:        boolean
  onClose:     () => void
  onCreated:   () => void
}

export default function NewTrialDialog({ open, onClose, onCreated }: Props) {
  const [studentEmail, setStudentEmail] = useState('')
  const [parentEmail,  setParentEmail]  = useState('')
  const [parentPhone,  setParentPhone]  = useState('')
  const [loading,      setLoading]      = useState(false)
  const [error,        setError]        = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setStudentEmail(''); setParentEmail(''); setParentPhone(''); setError(null)
  }, [open])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!studentEmail.trim() || !parentEmail.trim() || !parentPhone.trim()) {
      setError('All three fields are required.'); return
    }
    setLoading(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('submit-trial-lead', {
      body: {
        student_email: studentEmail.trim().toLowerCase(),
        parent_email:  parentEmail.trim().toLowerCase(),
        parent_phone:  parentPhone.trim(),
      },
    })
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
      setLoading(false); return
    }
    if (data?.error) {
      setError(data.error)
      setLoading(false); return
    }
    setLoading(false)
    onCreated()
    onClose()
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>+ New Trial Student</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The student will be invited via email and start a 60-day trial of Phase 1.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Student email" size="small" type="email" fullWidth required
            value={studentEmail} onChange={e => setStudentEmail(e.target.value)}
          />
          <TextField
            label="Parent email" size="small" type="email" fullWidth required
            value={parentEmail} onChange={e => setParentEmail(e.target.value)}
          />
          <TextField
            label="Parent phone" size="small" type="tel" fullWidth required
            value={parentPhone} onChange={e => setParentPhone(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
